import './calendar.scss'

import {
  DragAndDrop,
  Inject,
  Resize,
  ScheduleComponent,
  ViewDirective,
  ViewsDirective,
  Week,
} from '@syncfusion/ej2-react-schedule'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { extend } from '@syncfusion/ej2-base'
import { Spinner } from 'common/components/Spinner'
import dayjs from 'dayjs'
import CalendarEvent from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/CalendarEvent'
import {
  getAppointmentColor,
  getAppointmentColorInputFromEvent,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/helpers'
import { safeRefreshScheduleEvents } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/safeRefreshScheduleEvents'
import { MIN_PRE_POPULATE_TIME } from 'features/Home/constants/infoConstants'
import { useParams } from 'react-router'
import { useScheduleHeight } from '../useScheduleHeight'
import { ResourceHeader } from './ResourceHeader/ResourceHeader'
import type { ScheduleCalendarProps } from './types'
import { useScheduleWeekCalendar } from './useScheduleWeekCalendar'

export const ScheduleCalendarWeek = memo(
  ({
    createCustomAppointment,
    setEditedPatient,
    handleUpdateEvents,
    showConsultationStaffInitials = false,
    prepopulateTimeOnClick = MIN_PRE_POPULATE_TIME,
    dayInterval,
    weekDays = {},
    timeZone,
    isUpdating,
    schedules,
    patientStatuses,
    appointmentCategories,
    appointmentColor,
    hasEhrBooking,
  }: ScheduleCalendarProps) => {
    const { day } = useParams()
    const [interval, setInterval] = useState(15)
    const [isLoading, setIsLoading] = useState(false)

    const scheduleObj = useRef<ScheduleComponent>(null)
    const scheduleContainerRef = useRef<HTMLDivElement>(null)

    const formattedDate = useMemo(() => {
      if (!timeZone || !day) {
        return null
      }

      return dayjs(day).tz(timeZone, true).format('YYYY-MM-DD')
    }, [timeZone, day])

    const { events, handleScheduleChange, handleSelect } = useScheduleWeekCalendar({
      schedules,
      dayInterval,
      weekDays,
      formattedDate,
      timeZone,
      isUpdating,
      scheduleObj,
      prepopulateTimeOnClick,
      createCustomAppointment,
      handleUpdateEvents,
    })

    const data = useMemo(() => extend([], events, null, true), [events])

    useEffect(() => {
      const id = requestAnimationFrame(() => {
        safeRefreshScheduleEvents(scheduleObj.current)
      })
      return () => cancelAnimationFrame(id)
    }, [events])

    const handleZoomOut = useCallback(() => {
      setInterval((level) => (level < 15 ? level + 1 : level))
    }, [])

    const handleZoomIn = useCallback(() => {
      setInterval((level) => (level > 1 ? level - 1 : level))
    }, [])

    const indentTemplate = useCallback(() => {
      return
      // Required to remove, would suggest to keep commented
      // return <ZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
    }, [handleZoomIn, handleZoomOut])

    const eventRendered = useCallback(
      (props: any) => {
        const { statusCode, categoryCode } = getAppointmentColorInputFromEvent(
          props.data,
          schedules,
        )
        const { color, background, borderColor } = getAppointmentColor({
          appointmentColor,
          categoryStatuses: appointmentCategories,
          statusColorName: patientStatuses,
          statusCode,
          categoryCode,
        })
        props.element.style.color = color ?? ''
        props.element.style.background = background ?? ''
        props.element.style.border = `4px solid ${borderColor ?? 'var(--light-03)'}`
      },
      [appointmentColor, appointmentCategories, patientStatuses, schedules],
    )

    const eventTemplate = useCallback(
      (props: any) => {
        const { color, borderColor } = getAppointmentColor({
          appointmentColor,
          categoryStatuses: appointmentCategories,
          statusColorName: patientStatuses,
          statusCode: props.status?.code,
          categoryCode: props.category?.code,
        })
        return (
          <CalendarEvent
            event={props}
            statusColor={appointmentColor === 'status' ? color : borderColor}
            isWeekView
            hasEhrBooking={hasEhrBooking}
            showConsultationStaffInitials={showConsultationStaffInitials}
          />
        )
      },
      [
        appointmentColor,
        appointmentCategories,
        hasEhrBooking,
        patientStatuses,
        showConsultationStaffInitials,
      ],
    )

    const handleEventClick = useCallback((props: any) => {
      props.originalEvent.preventDefault()
      props.originalEvent.stopPropagation()
      setEditedPatient(props.event)
    }, [])

    const handleChange = useCallback(
      async (props: any) => {
        if (props.requestType !== 'eventChange') {
          return
        }

        setIsLoading(true)

        handleScheduleChange(props.data)
          .then(() => {
            setIsLoading(false)
            handleRemoveSelectedCells()
          })
          .catch(() => {
            setIsLoading(false)
            handleRemoveSelectedCells()
          })
      },
      [handleScheduleChange],
    )

    const handleRenderCell = useCallback(
      (args: any) => {
        if (args.elementType !== 'workCells') {
          return
        }

        // For Week view, we need to get the date from the cell
        const cellDate = dayjs(args.date).format('YYYY-MM-DD')
        const dayData = weekDays[cellDate]

        if (!dayData || !dayData.time_slots || dayData.time_slots.length === 0) {
          // If no day data or time slots, mark as disabled
          args.element.classList.add('e-disabled-cell')
          return
        }

        const cellStartTime = new Date(args.date)
        const cellEndTime = new Date(cellStartTime)

        // Check if this cell time falls within any of the available time slots
        const isAvailable = dayData.time_slots.some((slot: any) => {
          const slotStart = new Date(args.date)
          slotStart.setHours(
            parseInt(slot.start_time.split(':')[0], 10),
            parseInt(slot.start_time.split(':')[1], 10),
            0,
            0,
          )

          const slotEnd = new Date(args.date)
          slotEnd.setHours(
            parseInt(slot.end_time.split(':')[0], 10),
            parseInt(slot.end_time.split(':')[1], 10),
            0,
            0,
          )

          return cellStartTime >= slotStart && cellEndTime < slotEnd
        })

        if (!isAvailable) {
          args.element.classList.add('e-disabled-cell')
        }
      },
      [weekDays],
    )

    const majorSlotTemplate = useCallback((props: any) => {
      return <div className='e-time-slot-wrap'>{dayjs(props.date).format('h:mm A')}</div>
    }, [])

    const handlePopupOpen = useCallback((props: any) => {
      if (props.type === 'Editor') {
        props.cancel = true
      }
    }, [])

    const handleRemoveSelectedCells = useCallback(() => {
      document.querySelectorAll('.e-selected-cell')?.forEach((el) => {
        el.classList.remove('e-selected-cell')
      })
    }, [])

    const scheduleHeight = useScheduleHeight()

    return (
      <div className='schedule-control-section' ref={scheduleContainerRef}>
        <div className='col-lg-12 control-section'>
          <div className={`control-wrapper ${isLoading || !timeZone ? 'loading' : ''}`}>
            {timeZone && (
              <ScheduleComponent
                ref={scheduleObj}
                cssClass='week-schedule'
                width='100%'
                height={scheduleHeight}
                timezone={timeZone}
                timeScale={{ interval, slotCount: 1, majorSlotTemplate }}
                selectedDate={dayjs(day).tz(timeZone).toDate()}
                startHour={dayInterval.min as string}
                endHour={dayInterval.max as string}
                workHours={{
                  highlight: true,
                  start: dayInterval.min as string,
                  end: dayInterval.max as string,
                }}
                currentView='Week'
                headerIndentTemplate={indentTemplate}
                dateHeaderTemplate={ResourceHeader}
                eventRendered={eventRendered}
                showQuickInfo={false}
                showTimeIndicator={false}
                eventClick={handleEventClick}
                actionBegin={handleChange}
                showHeaderBar={false}
                select={handleSelect}
                popupOpen={handlePopupOpen}
                allowDragAndDrop
                eventSettings={{
                  dataSource: data,
                  enableTooltip: false,
                  enableIndicator: true,
                  fields: {
                    startTime: { title: 'From', name: 'start_time' },
                    endTime: { title: 'To', name: 'end_time' },
                  },
                }}
                renderCell={handleRenderCell}>
                <ViewsDirective>
                  <ViewDirective option='Week' eventTemplate={eventTemplate} />
                </ViewsDirective>
                <Inject services={[Week, Resize, DragAndDrop]} />
              </ScheduleComponent>
            )}
          </div>

          <div className='control-spinner'>
            <Spinner isLoading={isLoading} size='large' />
          </div>
        </div>
      </div>
    )
  },
)
